import { useMemo } from 'react';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getPageRoute } from '../utils';
import { useDepositButton } from '../pages/GamePage/GamePageMobile/hooks';
import {
  BONUS_DETAIL,
  BONUS_GAMES_WAGERING,
  BONUS_GAMES_COLLECTING,
  BONUSES_HISTORY,
  BONUSES_LIST,
  CAMPAIGN_DETAIL,
  CATEGORY_DETAIL,
  CATEGORY_LIST,
  COMMON_ERROR,
  FAVORITES,
  GAME_DETAIL,
  PROVIDER_DETAIL,
  PROVIDER_LIST,
  ROOT,
  SEARCH,
  THEME_DETAIL,
  THEME_LIST,
  PROVIDER_DETAIL_PAGINATED,
  CATEGORY_DETAIL_PAGINATED,
  THEME_DETAIL_PAGINATED,
  HALL_OF_FAME,
  HALL_OF_FAME_HISTORY,
} from '../constants/routeTemplates';
import {
  PageEvent,
  PageEventButton,
  StatusPageEvent,
} from '../redux/helpers/aba-models';
import { isNativeSelector } from '../redux/reducers/app/params';
import { useABAPage } from './useABAPage';
import { usePageTitle } from './usePageTitle';
import useNavbarCallback from './navbar/useNavbarCallback';

export const backButton = new PageEventButton('back-click').toObject();

const getMainPageEvent = (title, primaryButton = null) =>
  new PageEvent('main', title, primaryButton).toObject();

const getSearchPageEvent = (title, primaryButton = null) => {
  return new PageEvent('search', title, primaryButton, backButton).toObject();
};

const getFavoritesPageEvent = (title, primaryButton = null) => {
  return new PageEvent(
    'favorites',
    title,
    primaryButton,
    backButton
  ).toObject();
};
const getProviderListPageEvent = (title, primaryButton = null) => {
  return new PageEvent(
    'providers',
    title,
    primaryButton,
    backButton
  ).toObject();
};

const getProviderPageEvent = (title, primaryButton = null) => {
  return new PageEvent(
    'providerId',
    title,
    primaryButton,
    backButton
  ).toObject();
};

const getCategoryListPageEvent = (title, primaryButton = null) => {
  return new PageEvent(
    'categories',
    title,
    primaryButton,
    backButton
  ).toObject();
};

const getCategoryPageEvent = (title, primaryButton = null) => {
  return new PageEvent(
    'categoryId',
    title,
    primaryButton,
    backButton
  ).toObject();
};

const getThemeListPageEvent = (title, primaryButton = null) => {
  return new PageEvent('themes', title, primaryButton, backButton).toObject();
};

const getThemePageEvent = (title, primaryButton = null) => {
  return new PageEvent('themeId', title, primaryButton, backButton).toObject();
};

const getGamePageEvent = (title, primaryButton = null) => {
  return new PageEvent('gameId', title, primaryButton, backButton).toObject();
};

const getBonusPageEvent = (title, primaryButton = null) => {
  return new PageEvent('bonuses', title, primaryButton, backButton).toObject();
};

const getHallOfFameEvent = (title, primaryButton = null) => {
  return new PageEvent(
    'hallOfFame',
    title,
    primaryButton,
    backButton
  ).toObject();
};

const getHallOfFameHistoryEvent = (title, primaryButton = null) => {
  return new PageEvent('history', title, primaryButton, backButton).toObject();
};

const getBonusesHistoryPageEvent = (title, primaryButton = null) => {
  return new PageEvent('history', title, primaryButton, backButton).toObject();
};

const getBonusesGamesWageringPageEvent = (title, primaryButton = null) => {
  return new PageEvent(
    'bonusesGamesWagering',
    title,
    primaryButton,
    backButton
  ).toObject();
};

const getBonusesGamesCollectingPageEvent = (title, primaryButton = null) => {
  return new PageEvent(
    'bonusesGamesCollecting',
    title,
    primaryButton,
    backButton
  ).toObject();
};

const getBonusDetailPageEvent = (title, primaryButton = null) => {
  return new PageEvent('bonusId', title, primaryButton, backButton).toObject();
};

const getCampaignDetailEvent = (title, primaryButton = null) => {
  return new PageEvent(
    'campaignId',
    title,
    primaryButton,
    backButton
  ).toObject();
};

const getCommonErrorPageEvent = (title) => {
  return new StatusPageEvent(title).toObject();
};

export const PAGE_EVENT_CONFIG = {
  [ROOT]: getMainPageEvent,
  [SEARCH]: getSearchPageEvent,
  [FAVORITES]: getFavoritesPageEvent,

  [PROVIDER_LIST]: getProviderListPageEvent,
  [PROVIDER_DETAIL]: getProviderPageEvent,
  [PROVIDER_DETAIL_PAGINATED]: getProviderPageEvent,

  [CATEGORY_LIST]: getCategoryListPageEvent,
  [CATEGORY_DETAIL]: getCategoryPageEvent,
  [CATEGORY_DETAIL_PAGINATED]: getCategoryPageEvent,

  [THEME_LIST]: getThemeListPageEvent,
  [THEME_DETAIL]: getThemePageEvent,
  [THEME_DETAIL_PAGINATED]: getThemePageEvent,

  [GAME_DETAIL]: getGamePageEvent,

  [BONUSES_LIST]: getBonusPageEvent,
  [BONUSES_HISTORY]: getBonusesHistoryPageEvent,
  [BONUS_DETAIL]: getBonusDetailPageEvent,
  [CAMPAIGN_DETAIL]: getCampaignDetailEvent,
  [HALL_OF_FAME]: getHallOfFameEvent,
  [HALL_OF_FAME_HISTORY]: getHallOfFameHistoryEvent,

  [BONUS_GAMES_WAGERING]: getBonusesGamesWageringPageEvent,
  [BONUS_GAMES_COLLECTING]: getBonusesGamesCollectingPageEvent,

  [COMMON_ERROR]: getCommonErrorPageEvent,
};

const ROUTES = Object.keys(PAGE_EVENT_CONFIG);

const ROUTES_WITH_TITLE = [
  ROOT,
  SEARCH,
  GAME_DETAIL,
  BONUS_DETAIL,
  CAMPAIGN_DETAIL,
  PROVIDER_DETAIL,
  PROVIDER_DETAIL_PAGINATED,
  CATEGORY_DETAIL_PAGINATED,
  THEME_DETAIL_PAGINATED,
  PROVIDER_LIST,
  CATEGORY_LIST,
  THEME_LIST,
  FAVORITES,
  BONUSES_LIST,
  BONUSES_HISTORY,
  HALL_OF_FAME,
  HALL_OF_FAME_HISTORY,
];

export const usePageEvent = () => {
  const title = usePageTitle();
  const { pathname } = useLocation();
  const primaryButton = useDepositButton();

  const { path } = useRouteMatch();
  const isGamePage = path === GAME_DETAIL;

  const route = getPageRoute(pathname, ROUTES) || ROOT;
  const isNative = useSelector(isNativeSelector);

  const pageEventWithoutTitle = useMemo(
    () => PAGE_EVENT_CONFIG[route]('', primaryButton),
    [route, primaryButton]
  );
  const pageEventWithTitle = useMemo(
    () => PAGE_EVENT_CONFIG[route](title, primaryButton),
    [route, title, primaryButton]
  );

  /* For game page we have hook useGameNativeHeader(isDemoMode),
   * which has to manage pageEvent and send it rely on isDemoMode.
   * So we need to disable this one from sending duplicate of event.
   */
  if (isNative && isGamePage) {
    return null;
  }

  if (title) {
    const route = getPageRoute(pathname, ROUTES) || ROOT;

    if (!ROUTES_WITH_TITLE.includes(route)) {
      return pageEventWithoutTitle;
    }

    return pageEventWithTitle;
  }
};

export const useNativeHeader = () => {
  const pageEvent = usePageEvent();
  const callback = useNavbarCallback();
  useABAPage(pageEvent, true, callback);
};
